/**
 * Routes learned from how this fleet actually drives.
 *
 * The problem this solves, in the driver's own words: "we normally drive from
 * Surin towards Krasang and turn towards Prakhon Chai, instead of Prasat and
 * the 24 - same distance, fewer lights, better road."
 *
 * A routing engine will never offer that. `alternatives=true` returns small
 * deviations around its own optimum, not a different corridor through
 * different towns, so a genuinely better local route stays invisible however
 * many times you drive it.
 *
 * But the trip log already contains the answer: a recorded track through that
 * corridor. Pulling a couple of waypoints off a past trip and asking the
 * routing engine to go THROUGH them reconstructs the route the driver knows,
 * with live traffic and a proper ETA attached. It then competes with the
 * engine's own suggestions on signals, gradient and pace like any other
 * option - so local knowledge is proposed, never forced.
 */

import type { LngLat, Trip } from '../core/types';
import { distance } from '../core/geo';
import { readPoint } from './tripRecorder';

/** A past trip only informs this journey if it started near where we are. */
const SAME_ORIGIN_M = 25_000;
/** ...and ended near where we're going. */
const SAME_DEST_M = 1_000;
/** Two corridors this close together are the same road. */
const SAME_CORRIDOR_M = 3_000;
/** A track shorter than this says nothing useful about a corridor. */
const MIN_TRACK_POINTS = 8;

/** Where along a recorded track to lift waypoints from. */
const WAYPOINT_FRACTIONS = [0.35, 0.68];

function trackPoints(trip: Trip): LngLat[] {
  return (trip.track ?? []).map((p) => {
    const q = readPoint(p);
    return [q[0], q[1]] as LngLat;
  });
}

/**
 * Candidate via-waypoint sets drawn from previous drives to this destination.
 *
 * Returns at most `max` corridors, most recently driven first, each a small
 * list of waypoints to route through.
 */
export function learnedVias(
  trips: Trip[],
  origin: LngLat,
  destination: LngLat,
  max = 2,
): Array<{ tripId: string; vias: LngLat[] }> {
  const candidates: Array<{ tripId: string; vias: LngLat[]; at: number }> = [];

  for (const trip of trips) {
    if (!trip.destination || distance(trip.destination, destination) > SAME_DEST_M) continue;

    const pts = trackPoints(trip);
    if (pts.length < MIN_TRACK_POINTS) continue;

    // The corridor is only relevant if the drive began roughly where we are.
    const start = trip.origin ?? pts[0];
    if (distance(start, origin) > SAME_ORIGIN_M) continue;

    const vias = WAYPOINT_FRACTIONS.map((f) => pts[Math.floor(pts.length * f)]).filter(
      (p): p is LngLat => Array.isArray(p),
    );
    if (!vias.length) continue;

    // Waypoints sitting almost on top of the endpoints add nothing.
    if (vias.some((v) => distance(v, origin) < 2000 || distance(v, destination) < 2000)) continue;

    candidates.push({ tripId: trip.id, vias, at: trip.startedAt });
  }

  candidates.sort((a, b) => b.at - a.at);

  // Collapse repeats of the same road - a route driven weekly would otherwise
  // fill every slot with itself.
  const kept: Array<{ tripId: string; vias: LngLat[] }> = [];
  for (const c of candidates) {
    const duplicate = kept.some((k) =>
      k.vias.every((v, i) => c.vias[i] && distance(v, c.vias[i]) < SAME_CORRIDOR_M),
    );
    if (duplicate) continue;
    kept.push({ tripId: c.tripId, vias: c.vias });
    if (kept.length >= max) break;
  }
  return kept;
}
